import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileCheck,
  Search,
  Plus,
  Edit,
  Trash2,
  ExternalLink,
  X,
  Upload,
  Check,
  Eye,
  Phone,
  MapPin,
  Clock,
  MessageSquare,
  Loader
} from 'lucide-react';
import { notaryOfficesService } from '../services';
import { useToast } from '../context/ToastContext';
import './NotaryOfficesPage.css';

const NotaryOfficesPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [officeToDelete, setOfficeToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // API state
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, total: 1, count: 0 });
  const currentPage = pagination.current;
  const [saving, setSaving] = useState(false);
  // File upload state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phone: '',
    whatsapp: '',
    link: '',
    location: '',
    workingHours: '',
    isActive: true
  });

  // Fetch offices from API
  const fetchOffices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: 20
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await notaryOfficesService.getAll(params);

      if (response.success) {
        setOffices(response.data.offices || []);
        setPagination(response.data.pagination || { current: 1, total: 1, count: 0 });
      } else {
        setError(response.message || t('errors.fetchFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, currentPage, t]);

  useEffect(() => {
    fetchOffices();
  }, [fetchOffices]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const stats = useMemo(() => ({
    total: offices.length,
    active: offices.filter(o => o.isActive).length,
    inactive: offices.filter(o => !o.isActive).length
  }), [offices]);

  const handleAddOffice = () => {
    setFormData({
      name: '',
      description: '',
      phone: '',
      whatsapp: '',
      link: '',
      location: '',
      workingHours: '',
      isActive: true
    });
    setImageFile(null);
    setImagePreview(null);
    setIsEditing(false);
    setSelectedOffice(null);
    setShowModal(true);
  };

  const handleEditOffice = (office) => {
    setFormData({
      name: office.name,
      description: office.description,
      phone: office.phone,
      whatsapp: office.whatsapp,
      link: office.link,
      location: office.location,
      workingHours: office.workingHours,
      isActive: office.isActive
    });
    setImageFile(null);
    setImagePreview(office.image || null);
    setSelectedOffice(office);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleViewOffice = (office) => {
    setSelectedOffice(office);
    setShowViewModal(true);
  };

  const handleDeleteOffice = (office) => {
    setOfficeToDelete(office);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = useCallback(async () => {
    try {
      const response = await notaryOfficesService.delete(officeToDelete._id || officeToDelete.id);
      if (response.success) {
        fetchOffices();
        setShowDeleteConfirm(false);
        setOfficeToDelete(null);
      } else {
        toast.error(response.message || t('errors.deleteFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.deleteFailed'));
    }
  }, [officeToDelete, fetchOffices, t, toast]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let response;
      if (isEditing && selectedOffice) {
        response = await notaryOfficesService.update(selectedOffice._id || selectedOffice.id, formData, imageFile);
      } else {
        response = await notaryOfficesService.create(formData, imageFile);
      }

      if (response.success) {
        fetchOffices();
        setShowModal(false);
        setImageFile(null);
        setImagePreview(null);
      } else {
        toast.error(response.message || t('errors.saveFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [isEditing, selectedOffice, formData, imageFile, fetchOffices, t, toast]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const openWhatsApp = (whatsapp) => {
    window.open(`https://wa.me/${whatsapp}`, '_blank');
  };

  const openLink = (link) => {
    window.open(link, '_blank');
  };

  return (
    <div className="notary-offices-page">
      <div className="page-header">
        <div className="header-title">
          <FileCheck size={28} />
          <h1>{t('notaryOffices.title')}</h1>
          <span className="count">{stats.total}</span>
        </div>
        <button className="btn btn-primary" onClick={handleAddOffice}>
          <Plus size={18} />
          {t('notaryOffices.addOffice')}
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid stats-grid-3">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)' }}>
            <FileCheck size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">{t('notaryOffices.totalOffices')}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' }}>
            <Check size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.active}</span>
            <span className="stat-label">{t('notaryOffices.activeOffices')}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)' }}>
            <X size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.inactive}</span>
            <span className="stat-label">{t('notaryOffices.inactiveOffices')}</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="section-header">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder={t('notaryOffices.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <Loader size={32} className="spin" />
          <p>{t('common.loading')}</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="error-state">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchOffices}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('notaryOffices.officeImage')}</th>
                <th>{t('notaryOffices.officeName')}</th>
                <th>{t('notaryOffices.location')}</th>
                <th>{t('notaryOffices.phone')}</th>
                <th>{t('notaryOffices.workingHours')}</th>
                <th>{t('common.status')}</th>
                <th>{t('notaryOffices.contact')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {offices.map((office) => (
                <tr key={office._id || office.id}>
                  <td>
                    <div className="office-image">
                      {office.image ? (
                        <img src={office.image} alt={office.name} />
                      ) : (
                        <FileCheck size={24} />
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="office-name-cell">
                      <span className="office-name">{office.name}</span>
                      <span className="office-desc">{office.description?.substring(0, 50)}...</span>
                    </div>
                  </td>
                  <td>
                    <div className="location-cell">
                      <MapPin size={14} />
                      <span>{office.location}</span>
                    </div>
                  </td>
                  <td>
                    <div className="phone-cell">
                      <Phone size={14} />
                      <span>{office.phone}</span>
                    </div>
                  </td>
                  <td>
                    <div className="hours-cell">
                      <Clock size={14} />
                      <span>{office.workingHours}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${office.isActive ? 'status-active' : 'status-inactive'}`}>
                      {office.isActive ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td>
                    <div className="contact-buttons">
                      {office.whatsapp && (
                        <button 
                          className="contact-btn whatsapp"
                          onClick={() => openWhatsApp(office.whatsapp)}
                          title="WhatsApp"
                        >
                          <MessageSquare size={16} />
                        </button>
                      )}
                      {office.link && (
                        <button 
                          className="contact-btn link"
                          onClick={() => openLink(office.link)}
                          title="Website"
                        >
                          <ExternalLink size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn"
                        onClick={() => handleViewOffice(office)}
                        title={t('common.view')}
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => handleEditOffice(office)}
                        title={t('common.edit')}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="action-btn danger"
                        onClick={() => handleDeleteOffice(office)}
                        title={t('common.delete')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && offices.length === 0 && (
        <div className="empty-state">
          <FileCheck size={48} />
          <h3>{t('notaryOffices.noOffices')}</h3>
          <p>{t('notaryOffices.noOfficesDescription')}</p>
          <button className="btn btn-primary" onClick={handleAddOffice}>
            <Plus size={18} />
            {t('notaryOffices.addOffice')}
          </button>
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && pagination.total > 1 && (
        <div className="pagination">
          <button
            className="btn btn-outline"
            disabled={pagination.current === 1}
            onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
          >
            {t('common.previous')}
          </button>
          <span className="pagination-info">
            {t('common.page')} {pagination.current} {t('common.of')} {pagination.total}
          </span>
          <button
            className="btn btn-outline"
            disabled={pagination.current === pagination.total}
            onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal office-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditing ? t('notaryOffices.editOffice') : t('notaryOffices.addOffice')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>{t('notaryOffices.officeName')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>{t('notaryOffices.description')}</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('notaryOffices.phone')}</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('notaryOffices.whatsapp')}</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                      placeholder="966501234567"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('notaryOffices.location')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('notaryOffices.workingHours')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.workingHours}
                      onChange={(e) => setFormData({ ...formData, workingHours: e.target.value })}
                      placeholder="9:00 ص - 5:00 م"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>{t('notaryOffices.link')}</label>
                    <input
                      type="url"
                      className="form-input"
                      value={formData.link}
                      onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                      placeholder="https://"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>{t('notaryOffices.officeImage')}</label>
                    <div className="image-upload">
                      {imagePreview ? (
                        <div className="image-preview">
                          <img src={imagePreview} alt="Preview" />
                          <button 
                            type="button" 
                            className="remove-image"
                            onClick={handleRemoveImage}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <label className="upload-area">
                          <Upload size={24} />
                          <span>{t('notaryOffices.uploadImage')}</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            hidden
                          />
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      {t('notaryOffices.isActive')}
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <Loader size={18} className="spin" /> : null}
                  {isEditing ? t('common.save') : t('common.create')}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedOffice && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal office-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('notaryOffices.officeDetails')}</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="office-view">
                {selectedOffice.image && (
                  <div className="office-view-image">
                    <img src={selectedOffice.image} alt={selectedOffice.name} />
                  </div>
                )}
                <h3>{selectedOffice.name}</h3>
                <p className="office-description">{selectedOffice.description}</p>
                <div className="office-details-grid">
                  <div className="detail-item">
                    <MapPin size={16} />
                    <span>{selectedOffice.location}</span>
                  </div>
                  <div className="detail-item">
                    <Phone size={16} />
                    <span>{selectedOffice.phone}</span>
                  </div>
                  <div className="detail-item">
                    <Clock size={16} />
                    <span>{selectedOffice.workingHours}</span>
                  </div>
                  <div className="detail-item">
                    <span className={`status-badge ${selectedOffice.isActive ? 'status-active' : 'status-inactive'}`}>
                      {selectedOffice.isActive ? t('common.active') : t('common.inactive')}
                    </span>
                  </div>
                </div>
                <div className="office-actions">
                  {selectedOffice.whatsapp && (
                    <button 
                      className="btn btn-success"
                      onClick={() => openWhatsApp(selectedOffice.whatsapp)}
                    >
                      <MessageSquare size={18} />
                      WhatsApp
                    </button>
                  )}
                  {selectedOffice.link && (
                    <button 
                      className="btn btn-outline"
                      onClick={() => openLink(selectedOffice.link)}
                    >
                      <ExternalLink size={18} />
                      {t('notaryOffices.visitWebsite')}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowViewModal(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('notaryOffices.confirmDelete')}</h2>
              <button className="modal-close" onClick={() => setShowDeleteConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>{t('notaryOffices.deleteConfirmMessage', { name: officeToDelete?.name })}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-danger" onClick={confirmDelete}>
                {t('common.delete')}
              </button>
              <button className="btn btn-outline" onClick={() => setShowDeleteConfirm(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotaryOfficesPage;
